import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { apiWorkorders, team, automations } from '../../lib/api';
import { fmtDateUS, parseDate, nextNumber } from '../../lib/invoiceItems';
import { Bell, Truck, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useJobStatuses } from '../../lib/jobStatus';
import { todayISO } from '../../lib/dates';

import { today as localToday, ymd as localYmd, parseDay } from '../../lib/dates';
const FREQ = {
  none: 'Does not repeat',
  weekly: 'Weekly',
  biweekly: 'Bi-Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

const advance = (date, freq, i) => {
  const d = new Date(date);
  if (freq === 'weekly') d.setDate(d.getDate() + 7 * i);
  else if (freq === 'biweekly') d.setDate(d.getDate() + 14 * i);
  else if (freq === 'monthly') d.setMonth(d.getMonth() + i);
  else if (freq === 'quarterly') d.setMonth(d.getMonth() + 3 * i);
  return d;
};

export default function JobFormDialog({ open, setOpen, customer, editing, allWorkorders = [], onSaved }) {
  const [service, setService] = useState('');
  const [date, setDate] = useState(todayISO());
  const [tech, setTech] = useState('');
  const STATUSES = useJobStatuses();
  const [status, setStatus] = useState('Scheduled');
  const [notes, setNotes] = useState('');
  const [freq, setFreq] = useState('none');
  const [visits, setVisits] = useState(6);
  const [techs, setTechs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState('');

  const notify = async (type) => {
    setNotifying(type);
    try {
      const r = await automations.notifyWorkorder(editing.id, type);
      toast.success(`Sent via ${r.channels.join(', ')}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not notify customer');
    } finally { setNotifying(''); }
  };

  useEffect(() => {
    team.technicians().then(setTechs).catch(() => {});
  }, []);

  useEffect(() => {
    if (editing) {
      setService(editing.service || editing.name || '');
      const d = parseDate(editing.date);
      setDate(isNaN(d) ? localToday() : localYmd(d));
      setTech(editing.tech || '');
      setStatus(editing.status || 'Scheduled');
      setNotes(editing.notes || '');
      setFreq('none');
    } else {
      setService(''); setDate(todayISO());
      setTech(''); setStatus('Scheduled'); setNotes(''); setFreq('none'); setVisits(6);
    }
  }, [editing, open]);

  const baseDoc = (d, num) => ({
    id: `WO-${num}`,
    workOrderNumber: String(num),
    name: service,
    service,
    date: fmtDateUS(d),
    status,
    notes,
    tech,
    customer: customer?.name || editing?.customer || '',
    customerId: customer?.id || editing?.customerId || '',
    customerAccountNumber: customer?.accountNumber || editing?.customerAccountNumber || '',
    address: customer?.address || editing?.address || '',
    city: customer?.city || editing?.city || '',
    state: customer?.state || editing?.state || '',
    zip: customer?.zip || editing?.zip || '',
    recurrence: freq === 'none' ? '' : freq,
    createdDate: fmtDateUS(new Date()),
  });

  const submit = async () => {
    if (!service.trim()) { toast.error('Service / job type is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        // Editing keeps the job's own id and number: one update, nothing else.
        await apiWorkorders.update(editing.id, {
          ...editing, service, name: service, date: fmtDateUS(parseDay(date)), tech, status, notes,
        });
        toast.success('Job updated');
      } else {
        const startNum = nextNumber(allWorkorders, 1000);
        const start = parseDay(date);
        if (freq === 'none') {
          await apiWorkorders.create(baseDoc(start, startNum));
          toast.success('Job created');
        } else {
          const n = Math.max(1, Math.min(52, Number(visits) || 1));
          const docs = Array.from({ length: n }, (_, i) => baseDoc(advance(start, freq, i), startNum + i));
          await apiWorkorders.bulk(docs);
          toast.success(`${n} recurring jobs created (${FREQ[freq]})`);
        }
      }
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error('Could not save job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl" data-testid="job-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Job' : 'New Job / Work Order'}</DialogTitle>
          <DialogDescription>{customer ? `For ${customer.name}` : 'Schedule a service visit.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Service / Job Type</Label>
            <Input data-testid="job-service-input" value={service} onChange={(e) => setService(e.target.value)} className="mt-1.5" placeholder="e.g. Monthly Pest Service" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" data-testid="job-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
            <div>
              <Label>Technician</Label>
              <Select value={tech} onValueChange={setTech}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Assign…" /></SelectTrigger>
                <SelectContent>
                  {techs.map((t) => <SelectItem key={t.email} value={t.name}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.value}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!editing && (
              <div>
                <Label>Repeat</Label>
                <Select value={freq} onValueChange={setFreq}>
                  <SelectTrigger className="mt-1.5" data-testid="job-repeat-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(FREQ).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {!editing && freq !== 'none' && (
            <div>
              <Label>Number of visits</Label>
              <Input type="number" min={1} max={52} data-testid="job-visits-input" value={visits} onChange={(e) => setVisits(e.target.value)} className="mt-1.5" />
              <p className="text-xs text-slate-500 mt-1">Creates {Math.max(1, Math.min(52, Number(visits) || 1))} jobs, one every {FREQ[freq].toLowerCase()} interval starting {date}.</p>
            </div>
          )}
          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" rows={3} placeholder="Service notes, gate codes, pets, etc." />
          </div>
          {editing && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="job-notify-row">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Notify customer</div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" disabled={notifying} onClick={() => notify('reminder')} className="gap-1.5 bg-white" data-testid="notify-reminder-btn"><Bell className="w-3.5 h-3.5" />Reminder</Button>
                <Button type="button" size="sm" variant="outline" disabled={notifying} onClick={() => notify('on_the_way')} className="gap-1.5 bg-white" data-testid="notify-ontheway-btn"><Truck className="w-3.5 h-3.5" />On the way</Button>
                <Button type="button" size="sm" variant="outline" disabled={notifying} onClick={() => notify('review')} className="gap-1.5 bg-white" data-testid="notify-review-btn"><Star className="w-3.5 h-3.5" />Review request</Button>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Sends via SMS (Twilio) or email (Resend) to the customer. Configure templates in Settings → Automations.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="job-save-btn" onClick={submit} disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C]">
            {saving ? 'Saving…' : editing ? 'Save Job' : freq === 'none' ? 'Create Job' : 'Create Recurring Jobs'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
